import { Box, Grid, Typography } from '@mui/material';
import { formatCurrency } from '@acorn-portal/shared/src/utils';
import { FC, PropsWithChildren } from 'react';
import { FinancePlan, FinancePlanWithDeposit } from '@acorn-portal/shared/src/services/renewalsApi';

interface IMonthlyOption {
	rates: FinancePlanWithDeposit | FinancePlan;
	annualPremium: number;
	insuranceTaxPercentage: number;
	policyAdministrationFee: number;
	depositValue?: string;
}

const headerStyle = {
	fontSize: 16,
	height: { xs: 'auto', sm: 'auto' },
	marginX: 3,
	textAlign: 'left',
};

const valueStyle = {
	fontSize: 16,
	fontWeight: 600,
	textAlign: 'left',
	marginX: 3,
	color: 'secondary.main',
};

const WrapBox: FC<PropsWithChildren> = ({ children }) => (
	<Box
		sx={{
			flexDirection: 'column',
			justifyContent: 'end',
			height: '100%',
			display: 'flex',
		}}
	>
		{children}
	</Box>
);

const MonthlyPaymentSummary: FC<IMonthlyOption> = ({
	rates,
	annualPremium,
	insuranceTaxPercentage,
	policyAdministrationFee,
	depositValue,
}) => {
	const isUnequalInstallments = rates.unequalInstallmentAmount !== 0;
	const isDeposit = depositValue === 'yes';
	const financedPolicy = import.meta.env.VITE_AUTH_REALM !== 'Motorcade';

	const getMonthsTotal = () => {
		if (rates.unequalInstallmentAmount) return rates.installmentCount! - 1;
		return rates.installmentCount!;
	};

	const getHeadline = () => {
		if (isDeposit) {
			return 'Initial Payment Paid today';
		}

		return isUnequalInstallments ? 'First Payment of' : `${getMonthsTotal()} Monthly Payments of`;
	};

	const getHeadlineValue = () => {
		if (isDeposit) {
			return formatCurrency((rates as FinancePlanWithDeposit).depositAmount);
		}

		return isUnequalInstallments
			? formatCurrency(rates.unequalInstallmentAmount)
			: formatCurrency(rates.standardInstallmentAmount);
	};
	return (
		<Grid container>
			<Grid sx={{ width: '100%' }}>
				<Typography sx={{ fontSize: 20, mt: 2 }}>{getHeadline()}</Typography>
				<Typography sx={{ fontWeight: 'bold', fontSize: 35, color: 'secondary.main' }}>{getHeadlineValue()}</Typography>
				<Grid
					container
					sx={(theme) => ({
						[theme.breakpoints.up('md')]: {
							justifyContent: 'center',
						},
						mt: 2,
						rowGap: 2,
					})}
				>
					<Grid size={{ xs: 6, sm: 6, md: 2 }}>
						<WrapBox>
							<Typography sx={headerStyle}>Total</Typography>
							<Typography sx={valueStyle}>{formatCurrency(rates.totalPremium)}</Typography>
						</WrapBox>
					</Grid>
					{financedPolicy && (
						<>
							<Grid size={{ xs: 6, sm: 6, md: 2 }}>
								<WrapBox>
									<Typography sx={headerStyle}>Annual Premium</Typography>
									<Typography sx={valueStyle}>{formatCurrency(annualPremium)}</Typography>
								</WrapBox>
							</Grid>
							<Grid size={{ xs: 6, sm: 6, md: 2 }}>
								<WrapBox>
									<Typography sx={headerStyle}>Charge for credit</Typography>
									<Typography sx={valueStyle}>{formatCurrency(rates.chargeForCredit)}</Typography>
								</WrapBox>
							</Grid>
							<Grid size={{ xs: 6, sm: 6, md: 2 }}>
								<WrapBox>
									<Typography sx={headerStyle}>APR</Typography>
									<Typography sx={valueStyle}>{rates.apr}%</Typography>
								</WrapBox>
							</Grid>
						</>
					)}
					{isUnequalInstallments && isDeposit && (
						<Grid size={{ xs: 6, sm: 6, md: 2 }}>
							<WrapBox>
								<Typography sx={headerStyle}>First Payment</Typography>
								<Typography sx={valueStyle}>{formatCurrency(rates.unequalInstallmentAmount)}</Typography>
							</WrapBox>
						</Grid>
					)}
					{isUnequalInstallments && (
						<Grid size={{ xs: 6, sm: 6, md: 2 }}>
							<WrapBox>
								<Typography sx={headerStyle}>{getMonthsTotal()} Monthly Payments</Typography>
								<Typography sx={valueStyle}>{formatCurrency(rates.standardInstallmentAmount)}</Typography>
							</WrapBox>
						</Grid>
					)}
				</Grid>
				<br />
				<Typography
					sx={{
						fontSize: 14,
						borderTop: 1,
						borderColor: 'grey.300',
						pt: 2,
						mt: 0,
						paddingBottom: 2,
					}}
				>
					Prices include insurance premium tax of {insuranceTaxPercentage}% and a non-refundable policy administration
					fee of {formatCurrency(policyAdministrationFee)}
				</Typography>
			</Grid>
		</Grid>
	);
};

export default MonthlyPaymentSummary;
